import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  pointerWithin,
} from "@dnd-kit/core";
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { v4 as uuidv4 } from "uuid";
import { debounce } from "underscore";

import FormEditorHeader from "../headers/FormEditor";
import { Flex, message } from "antd";
import SiderRight from "./SiderRight";
import SiderLeft from "./SiderLeft";
import AssistantBot from "../ai-assistant/AssistantBot";
import { ASSISTANT_API_URL, SCOPE, listConditionsMessage, listFormsMessage } from "../ai-assistant/constants";
import "../ai-assistant/assistant-bot.css";
import "./form-editor.css";
import FormContainer from "./FormContainer";
// import { newWidgets, layoutWidgets } from './widgetsData';
import { setSelectedWidget } from "../../redux/slices/widgetsSlice";
import {
  getSingleMasterData,
  getMasterList,
  getRoles,
} from "../../services/adminServices";
import { showLoader } from "../../utils/showloader";
import {
  getFormCondition,
  getFormData,
  getSpaceWorkflow,
  saveNodeFormData,
  saveFormCOndition,
  deleteFormCondition,
  deleteForm,
} from "../../services/WorkflowServices";

import {
  findDuplicates,
  copyFormElement,
  findParentObjectById,
  getAllWidgets,
  getSectionChildId,
  recursiveDelete,
  recursiveInsert,
  recursiveUpdate,
  updateElementWidth,
  findMatch,
  findElementById,
  findParentWithChild,
  getFormattedFormItems,
  setFormItemDefaultAttribute,
} from "../../utils/formsHelper";
import { getUniqueValuesFromArray } from "../../utils/masterHelper";
import {
  setOriginalWorkflow,
  setWorkflow,
} from "../../redux/slices/connectorSlice";
import {
  setMasterList as setMasterListRedux,
} from "../../redux/slices/masterDataSlice";
import {
  getOrgStructure,
  getStructureDefinition,
} from "../../services/OrgStructure";
import { useTenantNavigation } from "../../hooks/useTenantNavigation";
import { useTranslation } from "react-i18next";

const FormEditor = () => {
  const { t } = useTranslation();

  const [formHeader, setFormHeader] = useState({
    label: t('Intake Form'),
    secondaryText: false,
    description: "",
    info: false,
    formInfo: "",
    hideHeader: false,
  });
  const [activeElement, setActiveElement] = useState(null);
  const [formType, setFormType] = useState("");
  const { selectedWidget, elements } = useSelector((state) => state.widgets);
  const { masterList } = useSelector((state) => state.masterData);
  const [formElements, setFormElements] = useState([]);
  const [masterData, setMasterData] = useState({});
  const [activeDragItem, setActiveDragItem] = useState(null);
  const [orgStructure, setOrgStructure] = useState([]);
  const [workflowSteps, setWorkflowSteps] = useState([])
  const [formConditions, setFormConditions] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tableName = searchParams.get("table");


  const { tableId, spaceId, workflowId, nodeId, formId } = useParams();
  const [currentFormId, setCurrentFormId] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [defaultValues, setDefaultValues] = useState({})
  const [validateSidebar, setValidateSidebar] = useState(null);
  const lastDefaultValuesRef = useRef("")
  // Always-current copy of defaultValues; the state mirror below is debounced.
  const defaultValuesRef = useRef({})
  const navigate = useTenantNavigation();
  
  const dispatch = useDispatch();
  const { userInfo, spaceUsers, userGroupSpaces } = useSelector((state) => state.user);
  const { spaceSpecificUsers = [], spaceSpecificGroups = [], spaceSpecificUserPickers = [] } = useSelector((state) => state.userRoleGroupState)

  useEffect(() => {
    dispatch(setOriginalWorkflow({}));
    dispatch(setSelectedWidget({}));
    dispatch(setWorkflow({ nodes: [], edges: [] }));
  }, []);

  const getWorkflow = async () => {
    showLoader(true)
    const response = await getSpaceWorkflow(spaceId);
    showLoader(false)
    if (response.status !== 200) {
      return;
    }
    if (response.data) {
      let steps = response.data?.node?.filter(curNode => curNode.type === "textInput" || curNode.type === "default" || (curNode.type === "circle" && curNode.data?.type === "end"))
      if (steps.length) {
        steps = steps.map(step => {
          return { data: step.data, label: step.data.name, value: step.id, nodeId: step.nodeId, formId: step?.formData?.formId || "", type: step.type }
        })
        setWorkflowSteps(steps)
      }

    }
  }

  useEffect(() => {
    if (workflowSteps.length === 0 && spaceId) {
      getWorkflow();
    }
  }, [])

  const fetchFormData = async () => {
    showLoader(true);
    let formResponse = await getFormData(formId);
    showLoader(false);
    if (formResponse.status === 200) {
      setFormHeader(formResponse.data?.formHeader || {});
      const formItems = getFormattedFormItems(formResponse.data?.items || [])
      setFormElements(formItems || []);
      applyDefaultValues(formResponse?.data?.defaultValues || {})
    }
  };

  useEffect(() => {
    if (formId && formId !== "new" && workflowSteps.length > 0) {
      let defaultNode = workflowSteps.find(step => step.type === "default")
      if(defaultNode) {
        let defaultFormId = defaultNode.formId
        if(defaultFormId === formId){
        setCurrentFormId(defaultFormId || "")

        }
      }
      fetchFormData();
    }
  }, [formId, workflowSteps]);

  const fetchFormConditions = async () => {
    let conditions = await getFormCondition(formId);
    if (conditions.status === 200) {
      setFormConditions(conditions?.data || []);
    } else {
      setFormConditions([]);
    }
  };

  useEffect(() => {
    if (formId && formId !== "new") {
      fetchFormConditions();
    }
  }, [formId]);

  const getMasterData = async () => {
    showLoader(true);
    let response = await getMasterList();
    showLoader(false);
    if (response.data) {
      dispatch(setMasterListRedux(response?.data || []));
    } else {
      dispatch(setMasterListRedux([]));
    }
  };

  const getTableData = async () => {
    let tableData = await getSingleMasterData(tableId);
    if (tableData.data) {
      setMasterData(tableData?.data || []);
      setFormElements(tableData?.data?.fields || []);
      applyDefaultValues(tableData?.data?.defaultValues || {});
      // The header widget is what edits a master table's name, so its per-language
      // text is carried in as `label` and written back out as `dataName` on save.
      const masterTranslations = tableData?.data?.translations;
      if (masterTranslations) {
        setFormHeader((prev) => ({
          ...prev,
          translations: Object.entries(masterTranslations).reduce((acc, [lng, bucket]) => {
            if (typeof bucket?.dataName === "string") {
              acc[lng] = { ...(prev.translations?.[lng] || {}), label: bucket.dataName };
            }
            return acc;
          }, { ...(prev.translations || {}) }),
        }));
      }
    } else {
      setMasterData([]);
    }
  };

  useEffect(() => {
    if (tableName) {
      setFormHeader((prev) => ({ ...prev, label: tableName }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (tableName) {
      getTableData();
    }
    getMasterData();
  }, [tableName]);

  const fetchOrgElements = async () => {
    let orgResponse = await getStructureDefinition(userInfo);

    if (orgResponse.status === 200 && orgResponse.data) {
      if (orgResponse.data.length > 0) {
        let strResponse = await getOrgStructure(userInfo);
        if (strResponse?.data?.[0]?.orgStructure) {
          let temp = [];
          let orgStructure = strResponse.data?.[0]?.orgStructure;
          if (orgStructure) {
            orgStructure = orgStructure;
          }
          orgResponse.data.map((r) => {
            let options = findMatch(orgStructure, "label", r.define);
            if (options) {
              options = options.map((o) => ({
                ...o,
                value: o.value,
                label: o.value,
                _label: o.label,
              }));
            }
            temp.push({
              label: r.define,
              id: "dropdown",
              isOrgStructure: true,
              formType: "dropdownField",
              options: options,
            });
          });
          setOrgStructure(temp);

          // setStructureAndOrgStructure(orgResponse.data, orgStructure)
        }
      }
    }
  };

  // Get Organisation Structure
  // useEffect(() => {
  //   if (userInfo?.userId && orgStructure.length === 0) {
  //     fetchOrgElements();
  //   }
  // }, [userInfo]);

  useEffect(() => {
    if (selectedWidget?.formType) {
      let clickedElement = { ...selectedWidget };
      if (clickedElement.formType) {
        setFormType(clickedElement.formType);
        delete clickedElement.formType;
      }
      setActiveElement({ ...clickedElement });
    } else {
      setActiveElement(null);
      setFormType("");
    }
  }, [selectedWidget]);

  // @dnd-kit sensors with improved configuration for smoother dragging
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Increased distance to prevent accidental drags
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  // Custom collision detection algorithm that prioritizes sections when dragging form elements
  const customCollisionDetection = (args) => {
    const { active } = args;
    const activeData = active?.data?.current;

    // One pass only: pointerWithin walks every registered droppable rect, and this
    // runs on every pointer move of a drag.
    const pointerCollisions = pointerWithin(args);

    // If dragging a form element, prioritize section drops over main form reordering
    if (activeData?.type === 'form-element') {
      const sectionCollisions = pointerCollisions.filter(collision => {
        const collisionId = String(collision.id);
        return collisionId.startsWith('section-') ||
          collisionId.startsWith('section-insert-');
      });

      if (sectionCollisions.length > 0) {
        return sectionCollisions;
      }
    }

    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    // Fallback to closestCenter which is recommended for sortable lists
    return closestCenter(args);
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveDragItem(active);
  };
  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Skip if trying to drop on itself
    if (activeId === overId) return;

    // Keep drag-over side-effect free to avoid re-render churn
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveDragItem(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;
    const dragData = active.data.current;
    const overData = over.data.current;
    const delta = event.delta;
    // Check if this was actually a drag (movement occurred) or just a click
    const isDrag = Math.abs(delta.x) > 2 || Math.abs(delta.y) > 2;
    const isClick = activeId === overId && !isDrag;

    // Handle click selection (when user clicks on an existing form element)
    if (isClick && activeId.length > 10) {
      // Find the clicked element
      const clickedElement = formElements.find(
        (element) => element.id === activeId
      );
      if (clickedElement) {
        // Create a clean copy without any React elements for Redux
        const cleanElement = {
          ...clickedElement,
          _selectionTime: Date.now(),
        };
        // Remove any React elements (like icons) before storing in Redux
        if (cleanElement.icon) {
          delete cleanElement.icon;
        }

        setActiveElement(cleanElement);
        setFormType(clickedElement.formType);
        // Store clean element in Redux
        dispatch(setSelectedWidget(cleanElement));
      }
      return; // Don't process as drag
    }

    // Clear activeElement only for actual drags
    if (isDrag) {
      setActiveElement(null);
    }

    let updatedFormElements = [...formElements];

    // Check if dragging an existing element vs new widget
    if (dragData && dragData.type === "widget") {
      // Adding new widgets from sidebar
      let item = dragData; // Use the data directly from the drag event

      let newItem = {
        ...item,
        id: uuidv4(),
        type: item.type === "widget" ? undefined : item.type, // Clean up the type if it's just 'widget'
      };

      if (item.id === "user") {
        newItem.isUserWidget = true;
      }

      // Clean up properties that shouldn't be in the form element
      if (newItem.icon) {
        delete newItem.icon;
      }
      if (newItem.type === undefined) {
        delete newItem.type;
      }
      if (newItem.formType === "ratingScaleField") {
        newItem = setFormItemDefaultAttribute(newItem);
      }

      // Handle insertion zones for positional placement
      if (overId.startsWith("insert-")) {
        const parts = overId.split("-");
        const position = parts[1]; // 'before' or 'after'
        const targetElementId = parts.slice(2).join("-"); // Handle UUIDs with dashes

        // Find the target element index
        const targetIndex = updatedFormElements.findIndex(element => element.id === targetElementId);

        if (targetIndex !== -1) {
          const insertIndex = position === "before" ? targetIndex : targetIndex + 1;
          updatedFormElements.splice(insertIndex, 0, newItem);
        } else {
          // Fallback to adding at the end
          updatedFormElements.push(newItem);
        }
      } else if (overId.startsWith("section-insert-")) {
        // Handle section insertion zones
        const parts = overId.split("-");
        const position = parts[2]; // 'before' or 'after' 
        const targetElementId = parts.slice(3).join("-"); // Handle UUIDs with dashes

        // Find the section that contains the target element
        let targetSection = null;
        let targetElementIndex = -1;

        // Try to get sectionId from droppable data first
        const sectionId = overData?.sectionId;

        if (sectionId) {
          targetSection = findElementById(updatedFormElements, sectionId);
        }

        // Fallback or specific check: if no sectionId in data, try to find parent by children
        if (!targetSection) {
          targetSection = findParentWithChild(updatedFormElements, targetElementId);
        }

        if (targetSection && targetSection.children) {
          const elementIndex = targetSection.children.findIndex(
            (child) => child.id === targetElementId
          );
          if (elementIndex !== -1) {
            targetElementIndex = elementIndex;
          }
        }

        if (targetSection) {
          let insertIndex = 0;
          if (targetElementIndex !== -1) {
            insertIndex = position === "before" ? targetElementIndex : targetElementIndex + 1;
          } else {
            // If section has no children or targeting empty section, append at end
            insertIndex = targetSection.children?.length || 0;
          }

          // Use recursiveInsert for immutable update
          updatedFormElements = recursiveInsert(updatedFormElements, String(targetSection.id), newItem, insertIndex);
        } else {
          // Fallback to adding at the end of the form if target not found or logic failed
          updatedFormElements = [...updatedFormElements, newItem];
        }
      } else if (overId.startsWith("section-")) {
        // Dropping into a section - use recursiveInsert to avoid state mutation
        const sectionId = overId.replace("section-", "");
        // Insert at the end of the section's children (index = -1 or children.length)
        const targetSection = findElementById(updatedFormElements, sectionId);
        const insertIndex = targetSection?.children?.length || 0;
        updatedFormElements = recursiveInsert(updatedFormElements, sectionId, newItem, insertIndex);
      } else if (overId === "main-form-droppable") {
        // Dropping into main form
        updatedFormElements.push(newItem);
      } else {
        // Dropping into main form (fallback)
        updatedFormElements.push(newItem);
      }

      setActiveElement(newItem);
      setFormType(item.formType);
    } else {
      // Moving existing elements - check if it's a UUID (which will be longer than typical widget IDs)
      const isExistingElement = activeId.length > 10; // UUIDs are much longer than widget IDs

      if (isExistingElement && isDrag) {
        // Handle the section-element case - moving between sections or out of a section
        if (dragData?.type === "section-element") {
          if (overId === "main-form-droppable") {
            // Moving from section to main form

            let draggedElement = null;
            const allItems = getAllWidgets(updatedFormElements);
            draggedElement = allItems.find(
              (element) => element.id === activeId
            );

            if (draggedElement) {
              // Remove from original section
              updatedFormElements = recursiveDelete(
                updatedFormElements,
                activeId
              );

              // Add to main form
              updatedFormElements.push(draggedElement);
            }
          } else if (overId.startsWith("section-")) {
            // Moving from one section to another section
            const targetSectionId = overId.replace("section-", "");

            // Skip if trying to move into the same section it's already in
            if (dragData.parentSectionId === targetSectionId) {
              return;
            }

            let draggedElement = null;
            const allItems = getAllWidgets(updatedFormElements);
            draggedElement = allItems.find(
              (element) => element.id === activeId
            );

            if (draggedElement) {

              // Remove from original section
              updatedFormElements = recursiveDelete(
                updatedFormElements,
                activeId
              );

              // Add to target section using recursiveInsert to avoid state mutation
              const targetSection = findElementById(updatedFormElements, targetSectionId);
              const insertIndex = targetSection?.children?.length || 0;
              updatedFormElements = recursiveInsert(updatedFormElements, targetSectionId, draggedElement, insertIndex);
            }
          }
        } else if (dragData?.type === "form-element") {
          // Moving form element from main form to section or reordering
          if (overId.startsWith("section-")) {
            // Moving from main form to section
            const sectionId = overId.replace("section-", "");

            // Prevent a section from being dropped into itself
            if (activeId === sectionId) {
              setActiveDragItem(null);
              return;
            }

            let draggedElement = null;
            const allItems = getAllWidgets(updatedFormElements);
            draggedElement = allItems.find(
              (element) => element.id === activeId
            );

            if (draggedElement) {
              // Also prevent sections from being dropped into sections (sections should only contain form elements)
              if (draggedElement?.formType === "section") {
                setActiveDragItem(null);
                return;
              }

              // Remove from main form
              updatedFormElements = recursiveDelete(
                updatedFormElements,
                activeId
              );

              // Add to section using recursiveInsert to avoid state mutation
              const sectionElement = findElementById(updatedFormElements, sectionId);
              const insertIndex = sectionElement?.children?.length || 0;
              updatedFormElements = recursiveInsert(updatedFormElements, sectionId, draggedElement, insertIndex);
            }
          } else {
            // Reordering within main form
            const activeIndex = updatedFormElements.findIndex(
              (item) => item.id === activeId
            );
            const overIndex = updatedFormElements.findIndex(
              (item) => item.id === overId
            );

            if (
              activeIndex !== -1 &&
              overIndex !== -1 &&
              activeIndex !== overIndex
            ) {
              updatedFormElements = arrayMove(
                updatedFormElements,
                activeIndex,
                overIndex
              );
            }
          }
        } // Handle reordering within sections
        else if (
          dragData?.type === "section-element" &&
          dragData.parentSectionId
        ) {
          const overData = over.data?.current;

          // Check if this is a cross-section movement or movement to main form
          const isCrossSectionMovement =
            overData?.type === "section-element" &&
            overData.parentSectionId !== dragData.parentSectionId;
          const isMovingToMainForm = overId === "main-form-droppable";
          const isMovingToSection =
            overId.startsWith("section-") &&
            overId !== `section-${dragData.parentSectionId}`;

          if (
            isCrossSectionMovement ||
            isMovingToMainForm ||
            isMovingToSection
          ) {

            let draggedElement = null;
            const allItems = getAllWidgets(updatedFormElements);
            draggedElement = allItems.find(
              (element) => element.id === activeId
            );

            if (draggedElement) {
              // Remove from original section
              updatedFormElements = recursiveDelete(
                updatedFormElements,
                activeId
              );

              if (isMovingToMainForm) {
                // Add to main form
                updatedFormElements.push(draggedElement);
              } else if (isMovingToSection) {
                // Add to target section using recursiveInsert to avoid state mutation
                const targetSectionId = overId.replace("section-", "");
                const targetSection = findElementById(updatedFormElements, targetSectionId);
                const insertIndex = targetSection?.children?.length || 0;
                updatedFormElements = recursiveInsert(updatedFormElements, targetSectionId, draggedElement, insertIndex);
              } else if (isCrossSectionMovement) {
                // Moving to another section via element hover
                // Insert at the correct position using recursiveInsert
                const targetSection = findElementById(updatedFormElements, overData.parentSectionId);
                // Find the index to insert after the target element
                const targetIndex = targetSection?.children?.findIndex(
                  (item) => item.id === overId
                );
                // If found, insert after; otherwise append at the end
                const insertIndex = targetIndex !== -1 ? targetIndex + 1 : (targetSection?.children?.length || 0);
                updatedFormElements = recursiveInsert(updatedFormElements, overData.parentSectionId, draggedElement, insertIndex);
              }
            }
          } else {
            // Handle internal section reordering (within the same section)

            const isInternalReorder =
              overData?.type === "section-element" &&
              overData?.parentSectionId === dragData.parentSectionId;

            if (isInternalReorder && activeId !== overId) {
              const activeIndex = dragData.index;
              const overIndex = overData?.index;

              if (activeIndex !== undefined && overIndex !== undefined) {
                // Call the section reorder handler
                handleSectionReorder(dragData.parentSectionId, activeIndex, overIndex);
              }
            }

            setActiveDragItem(null);
            return;
          }
        }
      }
    }
    setFormElements(updatedFormElements);
    setRenderKey(renderKey + 1);
    setActiveDragItem(null); // Clear the active drag item
  };
  // Handle reordering within a section
  const handleSectionReorder = useCallback((sectionId, activeIndex, overIndex) => {

    // Helper function to recursively find and reorder children
    const recursiveReorder = (items) => {
      return items.map((item) => {
        if (item.id === sectionId && item.children) {
          return {
            ...item,
            children: arrayMove(item.children, activeIndex, overIndex),
          };
        }
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: recursiveReorder(item.children),
          };
        }
        return item;
      });
    };

    setFormElements((prev) => recursiveReorder([...prev]));
    setRenderKey((prev) => prev + 1);
  }, []);

  const updateElement = (element) => {
    if (formType === "header") {
      // Merge so we don't drop fields the header form doesn't expose — notably
      // `translations`, which holds per-language header text. Keep activeElement
      // in sync too so switching the edit language mid-edit shows latest text.
      setFormHeader((prev) => ({ ...prev, ...element }));
      setActiveElement((prev) => ({ ...prev, ...element }));
      return;
    }
    // Option `score` is edited in the separate Scoring Panel, not in the sidebar
    // field editors (radio / multiple choice / dropdown only expose label+value).
    // When the sidebar serializes `options` it therefore drops `score`, so an
    // unrelated edit would wipe previously saved scores. Carry the existing score
    // forward (matched by option value) whenever the incoming option omits it.
    if (Array.isArray(element.options) && Array.isArray(activeElement?.options)) {
      element = {
        ...element,
        options: element.options.map((option) => {
          if (option && option.score === undefined) {
            const prev = activeElement.options.find(
              (p) => p && p.value === option.value
            );
            if (prev && prev.score !== undefined) {
              return { ...option, score: prev.score };
            }
          }
          return option;
        }),
      };
    }
    let updatedElements = recursiveUpdate(
      [...formElements],
      activeElement?.id,
      element
    );
    setFormElements(updatedElements);
    // Also update activeElement to keep it in sync
    setActiveElement({ ...activeElement, ...element });
  };
  const updateFieldOptions = (fieldId, options) => {
    // Use the functional updater so multiple calls in a row (e.g. saving scores
    // for several fields at once from the Scoring Panel) each build on the
    // previous result instead of a stale snapshot.
    setFormElements((prev) => recursiveUpdate(prev, fieldId, { options }));
    setActiveElement((prev) =>
      prev && prev.id === fieldId ? { ...prev, options } : prev
    );
  };

  // Read through a ref so this handler keeps a stable identity: it is passed into
  // every canvas field, and a new identity each render defeats their React.memo.
  const activeElementRef = useRef(activeElement);
  activeElementRef.current = activeElement;

  const widgetClickHandler = useCallback((element) => {

    // If clicking on the same element that's already active, don't reset it
    // This prevents losing unsaved changes in the sidebar form
    if (activeElementRef.current?.id === element.id) {
      return;
    }

    // Create a clean copy without any React elements for both local state and Redux
    const cleanElement = {
      ...element,
      _selectionTime: Date.now(),
    };

    // Remove any React elements (like icons) before storing
    if (cleanElement.icon) {
      delete cleanElement.icon;
    }

    // Always set the form type and active element
    setFormType(element.formType);
    setActiveElement(cleanElement);

    // Force re-selection by creating a new object with a timestamp
    // This ensures the useEffect will always trigger even for the same element
    dispatch(setSelectedWidget(cleanElement));
  }, [dispatch]);

  const handleWidgetWidth = useCallback((id) => {
    setFormElements((prev) => updateElementWidth([...prev], id));
  }, []);
  const handleWidgetDelete = useCallback((id) => {
    setFormElements((prev) => recursiveDelete([...prev], id));
    setActiveElement(null);
  }, []);

  const handleWidgetCopy = useCallback((element) => {
    setFormElements((prev) => {
    let updatedElements = [...prev];
    // Deep copy: the element, every descendant (nested sections included) and
    // every nested key (option values, table column ids, rating scale
    // questions) get fresh ids so the copy shares no id with the source.
    let reqElement = {
      ...copyFormElement(element),
      label: element.label + " Copy",
    };

    let parentWidget = findParentObjectById(updatedElements, element.id);
    if (parentWidget) {
      let elementIndexInSection = parentWidget.children?.findIndex(
        (child) => child.id === element.id
      );
      updatedElements = recursiveInsert(
        updatedElements,
        parentWidget.id,
        reqElement,
        elementIndexInSection + 1
      );
    } else {
      let indexOfElement = prev.findIndex(
        (ele) => element.id === ele.id
      );
      if (indexOfElement !== -1) {
        updatedElements.splice(indexOfElement + 1, 0, reqElement);
      }
    }
    return updatedElements;
    });
  }, []);

  const validateMinMaxCharacters = (items = []) => {
    for (const item of items) {
      if (!item) continue;

      if (item.formType === "inputForm") {
        const minValue = item.minNumber;
        const maxValue = item.maxNumber;
        const label = item.label || "Text field";

        const hasMin = minValue !== undefined && minValue !== null && String(minValue).trim() !== "";
        const hasMax = maxValue !== undefined && maxValue !== null && String(maxValue).trim() !== "";

        if (hasMin) {
          const minNum = Number(minValue);
          if (!Number.isInteger(minNum) || minNum < 0) {
            return {
              ok: false,
              message: `${label}: ${t("Enter a valid non-negative integer for Min Characters")}`,
            };
          }
          if (hasMax) {
            const maxNum = Number(maxValue);
            if (!Number.isInteger(maxNum) || maxNum < 0) {
              return {
                ok: false,
                message: `${label}: ${t("Enter a valid non-negative integer for Max Characters")}`,
              };
            }
            if (minNum >= maxNum) {
              return {
                ok: false,
                message: `${label}: ${t("Min Characters must be less than Max Characters")}`,
              };
            }
          }
        }

        if (hasMax) {
          const maxNum = Number(maxValue);
          if (!Number.isInteger(maxNum) || maxNum < 0) {
            return {
              ok: false,
              message: `${label}: ${t("Enter a valid non-negative integer for Max Characters")}`,
            };
          }
        }
      }

      if (Array.isArray(item.children) && item.children.length > 0) {
        const childResult = validateMinMaxCharacters(item.children);
        if (!childResult.ok) {
          return childResult;
        }
      }
    }

    return { ok: true };
  };

  const saveWorkflowForm = async (postData) => {
    const minMaxValidation = validateMinMaxCharacters(formElements || []);
    if (!minMaxValidation.ok) {
      message.error(minMaxValidation.message);
      return false;
    }

    // Validate that every form item has a label before attempting to save
    const flattenedItems = flattenFormElements(formElements || []);
    const missingLabels = flattenedItems.filter(
      (item) => !item.label || String(item.label).trim() === ""
    );
    if (missingLabels.length > 0) {
      message.error(
        `Please provide labels for all form items. ${missingLabels.length} item(s) missing.`
      );
      return false;
    }

    let duplicateItems = findDuplicates(formElements);
    duplicateItems = getUniqueValuesFromArray(duplicateItems, "label");

    if (duplicateItems.length > 0) {
      let duplicateLabels = duplicateItems.map((label) => label.label);
      message.error(
        "The form contains duplicate labels: " + duplicateLabels.join(", ")
      );
      return false;
    }

    showLoader(true);
    let finalData = {
      ...postData,
      spaceId: spaceId,
      items: formElements,
      formHeader: {
        label: formHeader.label,
        secondaryText: formHeader.secondaryText || false,
        description: formHeader.description || "",
        info: formHeader.info || false,
        formInfo: formHeader.formInfo || "",
        hideHeader: formHeader.hideHeader || false,
        translations: formHeader.translations || {},
      },
    };
    if (formId !== "new" && currentFormId !== "new") {
      finalData.formId = currentFormId || formId;
    }
    const saveResponse = await saveNodeFormData(finalData);
    showLoader(false);
    if(saveResponse.status === 200){
      let formattedItems = getFormattedFormItems(saveResponse.data.items || [])
      setFormElements(formattedItems);
    }

    if (saveResponse.status !== 200) {
      message.error(saveResponse.message);
      showLoader(false);
      return false;
    }
    let redirectUrl = `/app/space/${spaceId}/${workflowId}/${nodeId}/form-editor/${saveResponse?.data?.formId}`
    navigate(redirectUrl, { replace: true })

    message.success(t('Form saved successfully'));
    setCurrentFormId(saveResponse.data.formId);
    showLoader(false);
    return true;
  };

  const addMasterData = (data) => {
    let updatedFormElements = [...formElements];
    updatedFormElements = updatedFormElements.concat(data);
    setFormElements(updatedFormElements);
  };

  const flattenFormElements = (items = []) => {
    let flat = [];
    items.forEach((item) => {
      const { children, ...rest } = item || {};
      flat.push({ ...rest });
      if (Array.isArray(item.children) && item.children.length > 0) {
        flat = flat.concat(flattenFormElements(item.children));
      }
    });
    return flat
    // .map(item => ({id: item.id, label: item.label, options: item.options || [], formType: item.formType}));
  };

  const handleGenerateForm = async (prompt) => {
    const formBuilderBackendApi = process.env.REACT_APP_AI_FORM_BUILDER
    try {
      const isUpdate = Array.isArray(formElements) && formElements.length > 0;
      const endpoint = isUpdate
        ? `${formBuilderBackendApi}/api/forms/update`
        : `${formBuilderBackendApi}/api/forms/generate`;

      const requestPayload = isUpdate
        ? {
            prompt,
            form: {
              formHeader,
              items: formElements,
            },
          }
        : { prompt };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestPayload),
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (error) {
        payload = {};
      }
      if (!response.ok) {
        message.error(payload?.message || "Failed to generate form.");
        return { ok: false, message: payload?.message || "Failed to generate form." };
      }

      const data =
        payload?.data ||
        payload?.result ||
        (payload?.formHeader || payload?.items ? payload : {}) ||
        payload;
      const nextItems =
        data?.items || data?.formElements || data?.form?.items || [];
      const nextHeader = data?.formHeader || data?.form?.formHeader;
      const nextDefaults = data?.defaultValues || data?.form?.defaultValues;

      // if (!Array.isArray(nextItems) || nextItems.length === 0) {
      //   message.error("No form elements returned from generator.");
      //   return { ok: false };
      // }

      const usedIds = new Set();
      const normalizeIds = (items = []) =>
        items.map((item) => {
          const nextItem = { ...item };
          if (!nextItem.id || usedIds.has(nextItem.id)) {
            nextItem.id = uuidv4();
          }
          usedIds.add(nextItem.id);
          if (Array.isArray(nextItem.children) && nextItem.children.length > 0) {
            nextItem.children = normalizeIds(nextItem.children);
          }
          return nextItem;
        });

      setFormElements(normalizeIds(nextItems || []));
      if (nextHeader) {
        setFormHeader((prev) => ({ ...prev, ...nextHeader }));
      }
      if (nextDefaults) {
        applyDefaultValues(nextDefaults);
      }

      setActiveElement(null);
      setFormType("");
      dispatch(setSelectedWidget({}));
      setRenderKey((prev) => prev + 1);
      const apiMessage = payload?.message || data?.message;
      return { ok: true, message: apiMessage, isUpdate };
    } catch (error) {
      message.error(error?.message || "Failed to generate form.");
      return { ok: false };
    } finally {
    }
  };

  const handleApplyExtractedForm = (data) => {
    try {
      const payload = data?.data || data?.result || data;
      const nextItems =
        payload?.items || payload?.formElements || payload?.form?.items || [];
      const nextHeader = payload?.formHeader || payload?.form?.formHeader;
      const nextDefaults = payload?.defaultValues || payload?.form?.defaultValues;

      const usedIds = new Set();
      const normalizeItems = (items = []) =>
        items.map((item) => {
          const nextItem = { ...item };
          // API returns "type" but editor expects "formType"
          if (nextItem.type && !nextItem.formType) {
            nextItem.formType = nextItem.type;
            delete nextItem.type;
          }
          if (!nextItem.id || usedIds.has(nextItem.id)) {
            nextItem.id = uuidv4();
          }
          usedIds.add(nextItem.id);
          if (Array.isArray(nextItem.children) && nextItem.children.length > 0) {
            nextItem.children = normalizeItems(nextItem.children);
          }
          return nextItem;
        });

      setFormElements(normalizeItems(nextItems || []));
      setFormHeader((prev) => ({ ...prev, ...nextHeader }));
      if (nextDefaults) {
        applyDefaultValues(nextDefaults);
      }

      setActiveElement(null);
      setFormType("");
      dispatch(setSelectedWidget({}));
      setRenderKey((prev) => prev + 1);
      const apiMessage = payload?.message;
      message.success(apiMessage || "Form extracted from uploaded file.");
      return { ok: true, message: apiMessage };
    } catch (error) {
      message.error(error?.message || "Failed to apply extracted form.");
      return { ok: false };
    }
  };

  const handleGenerateConditions = async (prompt) => {
    const trimmedPrompt = (prompt || "").trim();
    if (!trimmedPrompt) {
      message.error(t('Please enter a prompt.'));
      return { ok: false };
    }
    let spaceUsers = spaceSpecificUsers.map(user => ({name: user.name, email: user.email}))
    let spaceGroups = userGroupSpaces.map(group => ({name: group.name, value: group.groupId}))
    try {
      const payload = {
        prompt: trimmedPrompt,
        form: flattenFormElements(formElements),
        workflowSteps: workflowSteps.map((s) => ({ label: s.label, value: s.value })),
        spaceUsers,
        spaceGroups
      };
      if(formConditions.length > 0 ) {
        payload.conditions = [...formConditions]
      }
      const formBuilderBackendApi = process.env.REACT_APP_AI_FORM_BUILDER

      const response = await fetch(
        `${formBuilderBackendApi}/api/conditions/upsert`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        data = {};
      }

      const generatedConditionsRaw =
        data?.data?.conditions ||
        data?.conditions ||
        data?.data ||
        data?.result ||
        data;
      const generatedConditions = Array.isArray(generatedConditionsRaw)
        ? generatedConditionsRaw
        : (generatedConditionsRaw && typeof generatedConditionsRaw === "object")
          ? [generatedConditionsRaw]
          : [];

      if (!response.ok) {
        message.error(data?.message || "Failed to generate conditions.");
        return { ok: false };
      }
      if (generatedConditions.length === 0) {
        message.error(t('No conditions returned from generator.'));
        return { ok: false };
      }

      const targetFormId = currentFormId || formId;
      const firstCondition = generatedConditions[0] || {};

      // Handle delete case
      if (firstCondition.delete === true && firstCondition.conditionId) {
        showLoader(true);
        const deleteResponse = await deleteFormCondition(firstCondition.conditionId);
        showLoader(false);

        if (deleteResponse.status !== 200) {
          message.error(deleteResponse.message || "Failed to delete condition.");
          return { ok: false };
        }
        fetchFormConditions();
        const deleteMsg = deleteResponse.message || data?.message || "Condition deleted successfully.";
        message.success(deleteMsg);
        return { ok: true, message: deleteMsg, data: deleteResponse };
      }

      const conditionPayload = {
        name: firstCondition.name || "Generated condition",
        scenarios: Array.isArray(firstCondition.scenarios) && firstCondition.scenarios.length > 0
          ? firstCondition.scenarios
          : [
              {
                operator: generatedConditions.length > 1 ? "&&" : "",
                conditions: generatedConditions.map((condition) => ({
                  reference: condition.reference,
                  comparator: condition.comparator,
                  value: condition.value,
                  parent: condition.parent,
                })),
              },
            ],
        actions: Array.isArray(firstCondition.actions) ? firstCondition.actions : [],
      };
      if(firstCondition.conditionId){
        conditionPayload.conditionId = firstCondition.conditionId
      }

      if (targetFormId && targetFormId !== "new") {
        conditionPayload.formId = targetFormId;
      }

      showLoader(true);
      const saveResponse = await saveFormCOndition(conditionPayload);
      showLoader(false);

      if (saveResponse.status !== 200) {
        message.error(saveResponse.message || "Failed to save generated condition.");
        return { ok: false, message: saveResponse.message || "Failed to save generated condition." };
      }

      const condMsg = data?.message || saveResponse.message || "Condition generated successfully.";
      message.success(condMsg);
      fetchFormConditions();
      return { ok: true, message: condMsg, data: saveResponse };
    } catch (error) {
      message.error(error?.message || "Failed to generate conditions.");
      return { ok: false };
    }
  };

  // The canvas pushes its whole value set up here on every keystroke. Writing the
  // ref is synchronous (so Save and the assistant payload always see the latest),
  // while the state mirror - which re-renders the entire editor - is debounced.
  // Every imperative setDefaultValues must go through applyDefaultValues so a
  // pending echo of the pre-write snapshot cannot land afterwards and revert it.
  const pushDefaults = useMemo(() => debounce(setDefaultValues, 300), [])
  useEffect(() => () => pushDefaults.cancel(), [pushDefaults])

  const passFormValues = useCallback((values={}) => {
    const safeValues = values || {}
    const nextString = JSON.stringify(safeValues)
    if (nextString === lastDefaultValuesRef.current) {
      return
    }
    lastDefaultValuesRef.current = nextString
    defaultValuesRef.current = safeValues
    pushDefaults(safeValues)
  }, [pushDefaults])

  const applyDefaultValues = useCallback((values = {}) => {
    pushDefaults.cancel()
    defaultValuesRef.current = values
    lastDefaultValuesRef.current = ""
    setDefaultValues(values)
  }, [pushDefaults])

  // ── Unified /ai assistant: builds the request payload and applies per-domain results ──

  const buildAssistantPayload = async (prompt) => {
    const slimUsers = (spaceSpecificUsers || []).map(u => ({
      name: u.name, email: u.email, userId: u.userId,
    }));
    const slimGroups = (spaceSpecificGroups || userGroupSpaces || []).map(g => ({
      name: g.name, value: g.groupId ?? g.value,
    }));
    const slimUserPickers = (spaceSpecificUserPickers || []).map(p => ({
      id: p.id, label: p.label, value: p.value ?? p.widgetId,
    }));
    let spaceRoles = [];
    try {
      const rolesData = await getRoles();
      spaceRoles = (Array.isArray(rolesData) ? rolesData : []).map(r => ({
        name: r.role || r.name || r.label,
        value: r.value || r.role,
      }));
    } catch (err) {
    }

    return {
      prompt,
      domain: "form",
      formItems: flattenFormElements(formElements),
      formConditions: formConditions || [],
      workflowNodes: [],
      spaceUsers: slimUsers,
      spaceGroups: slimGroups,
      spaceRoles,
      spaceUserPickers: slimUserPickers,
      // Ref, not the debounced state mirror: the assistant must see what is on the
      // canvas right now, not up to 300ms ago.
      formData: defaultValuesRef.current || defaultValues || {},
      workflowSteps: (workflowSteps || []).map(s => ({ label: s.label, value: s.value })),
    };
  };

  const applyFormFromAi = async (data, ctx) => {
    // Read-only "list" action: render the forms instead of mutating.
    if (String(ctx?.action).toLowerCase().trim() === "list") {
      return listFormsMessage(data);
    }

    // Delete can be signalled by ctx.action OR a `delete: true` flag on data.
    if (ctx?.action === "delete" || data?.delete === true) {
      const targetFormId = data?.formId || ctx?.raw?.formId || currentFormId || formId;
      if (!targetFormId || targetFormId === "new") {
        return { ok: true, skipped: true };
      }
      const deleteResp = await deleteForm(targetFormId);
      if (deleteResp?.status === 200) {
        setFormElements([]);
        setActiveElement(null);
        setFormType("");
        dispatch(setSelectedWidget({}));
        setRenderKey((prev) => prev + 1);
        return { ok: true, message: ctx?.raw?.message };
      }
      return { ok: false, message: deleteResp?.message || "Failed to delete form." };
    }

    const payload = data?.data || data?.result || data || {};
    const nextItems =
      payload.items || payload.formElements || payload.form?.items || [];
    const nextHeader = payload.formHeader || payload.form?.formHeader;
    const nextDefaults = payload.defaultValues || payload.form?.defaultValues;

    const usedIds = new Set();
    const normalizeIds = (items = []) =>
      items.map((item) => {
        const next = { ...item };
        if (next.type && !next.formType) {
          next.formType = next.type;
          delete next.type;
        }
        if (!next.id || usedIds.has(next.id)) {
          next.id = uuidv4();
        }
        usedIds.add(next.id);
        if (Array.isArray(next.children) && next.children.length > 0) {
          next.children = normalizeIds(next.children);
        }
        return next;
      });

    setFormElements(normalizeIds(nextItems));
    if (nextHeader) {
      setFormHeader((prev) => ({ ...prev, ...nextHeader }));
    }
    if (nextDefaults) {
      applyDefaultValues(nextDefaults);
    }

    setActiveElement(null);
    setFormType("");
    dispatch(setSelectedWidget({}));
    setRenderKey((prev) => prev + 1);
    return { ok: true };
  };

  const applyFormConditionsFromAi = async (data, ctx) => {
    // Read-only "list" action: render the conditions instead of mutating.
    if (String(ctx?.action).toLowerCase().trim() === "list") {
      return listConditionsMessage(data, "form");
    }

    const targetFormId = currentFormId || formId;
    // The AI may return either a single condition object or an array of them
    // (e.g. bulk delete returns [{ conditionId, delete: true }, ...]).
    const entries = Array.isArray(data) ? data : data ? [data] : [];
    let mutated = false;
    for (const entry of entries) {
      // Delete can be signalled by ctx.action OR a `delete: true` flag on the entry.
      const wantsDelete = ctx?.action === "delete" || entry?.delete === true;
      if (wantsDelete) {
        if (entry?.conditionId) {
          await deleteFormCondition(entry.conditionId);
          mutated = true;
        }
      } else if (entry) {
        const payload = { ...entry };
        if (targetFormId && targetFormId !== "new") {
          payload.formId = targetFormId;
        }
        await saveFormCOndition(payload);
        mutated = true;
      }
    }
    if (mutated) {
      await fetchFormConditions();
    }
    return { ok: true, skipped: !mutated };
  };

  return (
    <>
      <FormEditorHeader
        formId={currentFormId}
        headerData={formHeader}
        formElements={formElements}
        masterData={masterData}
        masterList={masterList}
        saveWorkflowForm={saveWorkflowForm}
        conditions={formConditions}
        fetchConditions={fetchFormConditions}
        setPreviewMode={setPreviewMode}
        addMasterData={addMasterData}
        orgStructureData={orgStructure}
        workflowSteps={workflowSteps}
        defaultValues={defaultValues}
        currentFormId={formId}
        validateSidebar={validateSidebar}
        onGenerateConditions={handleGenerateConditions}
        updateFieldOptions={updateFieldOptions}
      />{" "}
      <Flex className="flx-form-editor">
        {" "}
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
        >
          <SiderLeft onGenerateForm={handleGenerateForm} />{" "}
          <FormContainer
            formElements={formElements}
            formHeader={formHeader}
            formType={formType}
            activeElement={activeElement}
            activeDragItem={activeDragItem}
            widgetClickHandler={widgetClickHandler}
            handleWidgetWidth={handleWidgetWidth}
            handleWidgetDelete={handleWidgetDelete}
            handleWidgetCopy={handleWidgetCopy}
            handleSectionReorder={handleSectionReorder}
            conditions={formConditions}
            previewMode={previewMode}
            setPreviewMode={setPreviewMode}
            spaceId={spaceId} // key={"render-" + renderKey}
            nodeId={nodeId}
            workflowSteps={workflowSteps}
            passFormValues={passFormValues}
            defaultValues={defaultValues}
          />
          <DragOverlay>
            {activeDragItem ? (
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#fff",
                  border:
                    activeDragItem.data?.current?.type === "section-element"
                      ? "3px solid #1890ff"
                      : "2px solid #1890ff",
                  borderRadius: "8px",
                  boxShadow:
                    activeDragItem.data?.current?.type === "section-element"
                      ? "0 6px 16px rgba(24, 144, 255, 0.3)"
                      : "0 4px 12px rgba(0, 0, 0, 0.15)",
                  opacity: 0.95,
                  transform: "rotate(2deg)",
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    fontSize:
                      activeDragItem.data?.current?.type === "section-element"
                        ? "14px"
                        : "12px",
                    color:
                      activeDragItem.data?.current?.type === "section-element"
                        ? "#1890ff"
                        : "#666",
                    fontWeight:
                      activeDragItem.data?.current?.type === "section-element"
                        ? "600"
                        : "500",
                  }}
                >
                  {activeDragItem.data?.current?.type === "section-element" &&
                    "📤 "}
                  {activeDragItem.data?.current?.label ||
                    activeDragItem.data?.current?.formType ||
                    "Dragging..."}
                </div>
                {activeDragItem.data?.current?.type === "section-element" && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#1890ff",
                      fontWeight: "400",
                      marginTop: "2px",
                    }}
                  >
                    {t('Moving from section')}
                  </div>
                )}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
        <SiderRight
          activeElement={activeElement}
          formElements={formElements}
          updateElement={updateElement}
          formType={formType}
          setValidateSidebar={setValidateSidebar}
          defaultValues={defaultValues}
        />
      </Flex>

      {/* <AssistantBot
        title="AI Form Assistant"
        welcomeMessage="Hi! I can help you generate forms or set up conditions. What would you like to do?"
        placeholder="Describe what you'd like to build"
        apiUrl={ASSISTANT_API_URL}
        allowedDomains={SCOPE.FORM_BUILDER}
        handlers={{
          form: (data, ctx) => applyFormFromAi(data, ctx),
          formCondition: (data, ctx) => applyFormConditionsFromAi(data, ctx),
        }}
        buildPayload={buildAssistantPayload}
        fileUpload={{
          uploadUrl: `${process.env.REACT_APP_AI_FORM_BUILDER}/api/forms/upload-extract`,
          onApplyExtracted: handleApplyExtractedForm,
        }}
      /> */}
    </>
  );
};

export default FormEditor;
